import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Calendar, Check, Clock } from 'lucide-react';
import { cn } from '@/lib/cn';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface AppointmentSlot {
  id: string;
  userId: string;
  startsAt: string;
  endsAt: string;
  status: string;
  durationMinutes: number;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function PublicBookingPage() {
  const { tenantSlug, userSlug } = useParams<{ tenantSlug: string; userSlug: string }>();
  const { t } = useTranslation();

  const [slots, setSlots] = useState<AppointmentSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    if (!tenantSlug || !userSlug) return;
    setLoading(true);
    try {
      const res = await apiClient.get<AppointmentSlot[]>(
        `/public/book/${tenantSlug}/${userSlug}/slots`,
      );
      setSlots(res.data);
    } catch {
      setError('Could not load available slots.');
    } finally {
      setLoading(false);
    }
  }, [tenantSlug, userSlug]);

  useEffect(() => {
    load();
  }, [load]);

  async function book() {
    if (!selectedSlot || !name.trim() || !email.trim()) return;
    setBooking(true);
    setError(null);
    try {
      await apiClient.post(`/public/book/${tenantSlug}/${userSlug}`, {
        slotId: selectedSlot,
        attendeeName: name.trim(),
        attendeeEmail: email.trim(),
        notes: notes.trim() || undefined,
      });
      setBooked(true);
    } catch {
      setError('Could not complete booking. The slot may have been taken — please pick another.');
      load();
    } finally {
      setBooking(false);
    }
  }

  // Group slots by date
  const slotsByDate = slots.reduce<Record<string, AppointmentSlot[]>>((acc, slot) => {
    const date = new Date(slot.startsAt).toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
    (acc[date] = acc[date] ?? []).push(slot);
    return acc;
  }, {});

  /* ---- Booked confirmation ---- */
  if (booked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-background to-muted/20 p-6">
        <div className="w-full max-w-sm rounded-2xl border bg-card p-8 text-center shadow-lg">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-900/40">
            <Check className="h-7 w-7 text-emerald-600" />
          </div>
          <h2 className="text-xl font-semibold">{t('appointments.book_success')}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            A confirmation has been sent to <strong>{email}</strong>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <div className="mx-auto max-w-2xl px-4 py-10">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Calendar className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-2xl font-bold">{t('appointments.book')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Select a time that works for you.
          </p>
        </div>

        {loading && (
          <p className="text-center text-sm text-muted-foreground">Loading available times...</p>
        )}

        {error && (
          <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {!loading && slots.length === 0 && !error && (
          <div className="rounded-xl border bg-card py-10 text-center text-sm text-muted-foreground">
            No available slots at this time.
          </div>
        )}

        {!loading && slots.length > 0 && (
          <div className="grid gap-8 md:grid-cols-2">
            {/* Slot picker */}
            <div>
              <h2 className="mb-4 text-sm font-semibold">{t('appointments.date_time')}</h2>
              <div className="space-y-4">
                {Object.entries(slotsByDate).map(([date, daySlots]) => (
                  <div key={date}>
                    <p className="mb-2 text-xs font-medium text-muted-foreground">{date}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {daySlots.map((slot) => {
                        const time = new Date(slot.startsAt).toLocaleTimeString(undefined, {
                          hour: 'numeric',
                          minute: '2-digit',
                        });
                        const isSelected = selectedSlot === slot.id;
                        return (
                          <button
                            key={slot.id}
                            onClick={() => setSelectedSlot(slot.id)}
                            className={cn(
                              'flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-colors',
                              isSelected
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'bg-card hover:border-primary hover:bg-primary/5',
                            )}
                          >
                            <Clock className="h-3.5 w-3.5 shrink-0" />
                            <span>{time}</span>
                            <span className="text-[10px] opacity-70">
                              {slot.durationMinutes}m
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Booking form */}
            <div>
              <h2 className="mb-4 text-sm font-semibold">Your details</h2>
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-medium">Name *</label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium">Email *</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium">Notes (optional)</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Anything you'd like us to know in advance..."
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
                <Button
                  onClick={book}
                  disabled={!selectedSlot || !name.trim() || !email.trim() || booking}
                  className="w-full"
                >
                  {booking ? 'Booking...' : t('appointments.book')}
                </Button>
                {!selectedSlot && (
                  <p className="text-center text-xs text-muted-foreground">
                    Please select a time slot first.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
